import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Circle, Polygon } from '@react-google-maps/api';
import { leadGenService } from '../../services/leadGeneration';
import { useAuth } from '../../context/AuthContext';
import { formatPlaceTypeDisplay } from '../../utils/supportedPlaceTypes';
import ChainDetectionModal from './ChainDetectionModal';
import CircleAnalysisModal from './CircleAnalysisModal';
import ConfirmationModal from '../common/ConfirmationModal';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import { GOOGLE_MAPS_CONFIG, mapContainerStyle } from '../../config/googleMapsConfig';
import GuideSystem from '../common/GuideSystem';
import FloatingGuideButton from '../common/FloatingGuideButton';
import { getGuideContent } from '../../utils/guideContent';
import { useGuide } from '../../hooks/useGuide';
import SearchResultsPanel from './SearchResultsPanel';
import MapView from './MapView';
import SearchSidebar from './SearchSidebar';
import * as ShapeUtils from '../../utils/shapeManipulationUtils';
import * as AreaUtils from '../../utils/searchAreaUtils';
import { storageService } from '../../utils/storage';
import { useNavigate } from 'react-router-dom';


// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// City coordinates for quick navigation
const CITIES = [
  { name: 'New York City, United States', lat: 40.7128, lng: -74.0060 },
  { name: 'London, United Kingdom', lat: 51.5074, lng: -0.1278 },
  { name: 'Istanbul, Turkey', lat: 41.0082, lng: 28.9784 },
  { name: 'Sydney, Australia', lat: -33.8688, lng: 151.2093 },
  { name: 'São Paulo, Brazil', lat: -23.5505, lng: -46.6333 },
  { name: 'Tokyo, Japan', lat: 35.6762, lng: 139.6503 },
];

const SearchPage = () => {
  const navigate = useNavigate();
  const { user, getRemainingRequests, getRequestLimit, getAreaLimit, getUserType } = useAuth();
  const [map, setMap] = useState(null);
  const [searchedAreas, setSearchedAreas] = useState([]);
  const [unsearchedAreas, setUnsearchedAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [currentRadius, setCurrentRadius] = useState(() => {
    const saved = localStorage.getItem('leadgen_radius');
    return saved ? parseInt(saved) : 1000;
  });
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [lastSearchSession, setLastSearchSession] = useState(null);
  const [includedTypes, setIncludedTypes] = useState(() => {
    const saved = localStorage.getItem('leadgen_included_types');
    return saved ? JSON.parse(saved) : ['restaurant'];
  });
  const [excludedTypes, setExcludedTypes] = useState(() => {
    const saved = localStorage.getItem('leadgen_excluded_types');
    return saved ? JSON.parse(saved) : [];
  });
  const [mapCenter, setMapCenter] = useState({ lat: 51.5074, lng: -0.1278 });
  const [hasInitialized, setHasInitialized] = useState(false);
  const searchResultsRef = useRef(null);
  const [circleAnalysis, setCircleAnalysis] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [chainDetection, setChainDetection] = useState(null);
  const [showChainModal, setShowChainModal] = useState(false);

  // Polygon functionality state
  const [searchMode, setSearchMode] = useState('circles');
  const [currentShape, setCurrentShape] = useState(null);
  const [generatedCircles, setGeneratedCircles] = useState([]);
  const [selectedPolygonCircles, setSelectedPolygonCircles] = useState(new Set());
  const [isGeneratingCircles, setIsGeneratingCircles] = useState(false);
  const [polygonRadius, setPolygonRadius] = useState(() => {
    const saved = localStorage.getItem('leadgen_polygon_radius');
    return saved ? parseInt(saved) : 2000;
  });

  // Custom radius input states
  const [customRadiusInput, setCustomRadiusInput] = useState('');
  const [showCustomRadiusInput, setShowCustomRadiusInput] = useState(false);
  const [customPolygonRadiusInput, setCustomPolygonRadiusInput] = useState('');
  const [showCustomPolygonRadiusInput, setShowCustomPolygonRadiusInput] = useState(false);

  // Viewport filtering state
  const [mapBounds, setMapBounds] = useState(null);
  const [visibleAreas, setVisibleAreas] = useState([]);

  const [isDrawingMode, setIsDrawingMode] = useState(false);
  
  // Polygon shape state
  const [isDragging, setIsDragging] = useState(false);
  const [shapeSize, setShapeSize] = useState(0.02);
  const [tubeThickness, setTubeThickness] = useState(0.01);
  
  // Shape interaction mode and undo state
  const [shapeInteractionMode, setShapeInteractionMode] = useState('move');
  const [shapeHistory, setShapeHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  
  // Shape dragging state
  const [isShapeDragging, setIsShapeDragging] = useState(false);
  const [shapeDragStart, setShapeDragStart] = useState(null);
  
  // Previously searched shapes state
  const [searchedShapes, setSearchedShapes] = useState([]);
  
  // Track if this is an active session
  const [hasSearchedThisSession, setHasSearchedThisSession] = useState(false);

  const [filterSearchMode, setFilterSearchMode] = useState(() => {
    return AreaUtils.getFilterSearchMode();
  });

  const [textQuery, setTextQuery] = useState(() => {
    return AreaUtils.getTextQuery();
  });
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'default',
    confirmText: 'OK',
    cancelText: 'Cancel'
  });

  // Social media notification state
  const [showSocialMediaNotification, setShowSocialMediaNotification] = useState(false);

  const { isGuideVisible, hasSeenGuide, showGuide, hideGuide } = useGuide('search');
  const guideData = getGuideContent('search');

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  // Show social media notification for new users after saving
  useEffect(() => {
    if (showSocialMediaNotification) {
      const notificationToast = toast(
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
            <span className="font-semibold text-gray-900">Searching for Social Media Links</span>
          </div>
          <p className="text-sm text-gray-600">
            We're now finding social media accounts for your saved places.
          </p>
          <button
            onClick={() => {
              navigate('/places');
              toast.dismiss(notificationToast);
            }}
            className="mt-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium transition-colors text-left"
          >
            Go to Saved Places →
          </button>
        </div>,
        {
          duration: 3000,
          icon: '🔍',
          style: {
            minWidth: '350px',
            background: '#eff6ff',
            border: '1px solid #3b82f6',
          },
        }
      );

      // Reset the flag after showing
      setTimeout(() => {
        setShowSocialMediaNotification(false);
      }, 3000);
    }
  }, [showSocialMediaNotification, navigate]);


  // Undo functionality
  const undoShapeChange = () => {
    ShapeUtils.undoShapeChange(historyIndex, shapeHistory, setHistoryIndex, setCurrentShape, toast);
  };

  // Clear localStorage on mount to start fresh session
  useEffect(() => {
    const hasUnsaved = storageService.getUnsavedSearchResults();
    if (!hasUnsaved) {
      storageService.removeUnsavedSearchResults();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (searchResults.length > 0) {
        storageService.setUnsavedSearchResults(searchResults);
      }
    };
  }, [searchResults]);

  useEffect(() => {
    if (searchResults.length > 0) {
      storageService.setUnsavedSearchResults(searchResults);
    }
  }, [searchResults]);

  useEffect(() => {
    return () => {
      if (searchResults.length > 0 && hasSearchedThisSession) {
        storageService.setUnsavedSearchResults(searchResults);
      }
    };
  }, [searchResults, hasSearchedThisSession]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey) {
        setIsCtrlPressed(true);
        if (event.key === 'z' || event.key === 'Z') {
          event.preventDefault();
          undoShapeChange();
        }
      }
      
      if (event.key === 'm' || event.key === 'M') {
        setShapeInteractionMode(prev => prev === 'move' ? 'reshape' : 'move');
        toast(`Shape interaction mode: ${shapeInteractionMode === 'move' ? 'Reshape' : 'Move'}`, {
          icon: 'ℹ️',
          duration: 2000
        });
      }
    };

    const handleKeyUp = (event) => {
      if (!event.ctrlKey) {
        setIsCtrlPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [shapeInteractionMode, historyIndex]);

  useEffect(() => {
    ShapeUtils.initializeShapeHistory(currentShape, shapeHistory, setShapeHistory, setHistoryIndex);
  }, [currentShape?.center]);

  const createPentagonPoints = (center, radius) => {
    const lat = center.lat;
    const lng = center.lng;
    const points = [];
    
    for (let i = 0; i < 5; i++) {
      const angle = (i * 72 - 90) * (Math.PI / 180);
      const pointLat = lat + radius * Math.cos(angle);
      const pointLng = lng + radius * Math.sin(angle) / Math.cos(lat * Math.PI / 180);
      points.push({ lat: pointLat, lng: pointLng });
    }
    
    return points;
  };

  useEffect(() => {
    if (currentShape && currentShape.center) {
      ShapeUtils.updateShapeSize(currentShape, searchMode, shapeSize, tubeThickness, setCurrentShape);
    }
  }, [shapeSize]);

  useEffect(() => {
    if (currentShape && searchMode === 'tube' && currentShape.center) {
      ShapeUtils.updateTubeThickness(currentShape, searchMode, tubeThickness, setCurrentShape);
    }
  }, [tubeThickness]);

  useEffect(() => {
    loadSearchedShapes();
  }, []);

  const loadSearchedShapes = async () => {
    try {
      console.log('Loading searched shapes...');
      const { data, error } = await leadGenService.getSearchShapes();
      if (data) {
        const shapes = [
          ...data.searched_shapes?.areas?.filter(area => area.shape_data) || [],
          ...data.unsearched_shapes?.areas?.filter(area => area.shape_data) || []
        ];
        
        // console.log('Loaded searched shapes:', shapes);
        setSearchedShapes(shapes);
      } else if (error) {
        console.error('Error loading searched shapes:', error);
      }
    } catch (error) {
      console.error('Error loading searched shapes:', error);
    }
  };

  useEffect(() => {
    if (!isSearching && searchResults.length > 0) {
      setTimeout(() => {
        loadSearchedShapes();
      }, 1000);
    }
  }, [isSearching, searchResults.length]);

  const isAreaInBounds = (area, bounds) => {
    if (!bounds) return true;
    
    const areaLat = parseFloat(area.latitude);
    const areaLng = parseFloat(area.longitude);
    
    const north = bounds.getNorthEast().lat();
    const south = bounds.getSouthWest().lat();
    const east = bounds.getNorthEast().lng();
    const west = bounds.getSouthWest().lng();
    
    return areaLat >= south && areaLat <= north && areaLng >= west && areaLng <= east;
  };

  const filterAreasInViewport = () => {
    if (!mapBounds) {
      setVisibleAreas(getAllAreas());
      return;
    }
    
    const allAreas = getAllAreas();
    const filtered = allAreas.filter(area => isAreaInBounds(area, mapBounds));
    setVisibleAreas(filtered);
  };

  const handleBoundsChanged = () => {
    if (map) {
      const bounds = map.getBounds();
      setMapBounds(bounds);
    }
  };

  const debouncedBoundsChanged = useRef(null);

  useEffect(() => {
    debouncedBoundsChanged.current = debounce(handleBoundsChanged, 300);
  }, []);

  useEffect(() => {
    filterAreasInViewport();
  }, [mapBounds, searchedAreas, unsearchedAreas]);

  const handleShapeDelete = () => {
    ShapeUtils.handleShapeDelete(setCurrentShape, setGeneratedCircles, setSelectedPolygonCircles, setShapeHistory, setHistoryIndex);
  };

  const handleDeleteSpecificArea = async (areaId) => {
  await AreaUtils.deleteSpecificArea(areaId, selectedArea, setUnsearchedAreas, setSelectedArea);
};

  const handleGenerateShapeCircles = async () => {
    if (!currentShape) {
      toast.error('Please create a shape first');
      return;
    }

    setIsGeneratingCircles(true);
    
    try {
      let response;
      
      if (currentShape.type === 'pentagon') {
        const polygonCoords = currentShape.outerPoints.map(point => ({
          latitude: point.lat,
          longitude: point.lng
        }));

        response = await leadGenService.generatePolygonCircles(
          polygonCoords,
          polygonRadius,
          'polygon'
        );
      } else if (currentShape.type === 'tube') {
        const outerCoords = currentShape.outerPoints.map(point => ({
          latitude: point.lat,
          longitude: point.lng
        }));
        
        const innerCoords = currentShape.innerPoints.map(point => ({
          latitude: point.lat,
          longitude: point.lng
        }));

        response = await leadGenService.generateTubeCircles(
          outerCoords,
          innerCoords,
          polygonRadius
        );
      }

      const { data, error } = response;

      if (data) {
        setGeneratedCircles(data.circles || []);
        setSelectedPolygonCircles(new Set());
        toast.success(`Generated ${data.total_circles} circles`);
      } else if (error) {
        toast.error('Failed to generate circles');
        console.error('Error generating circles:', error);
      }
    } catch (error) {
      toast.error('Failed to generate circles');
      console.error('Error:', error);
    }
    
    setIsGeneratingCircles(false);
  };

  const handlePolygonCircleSelection = (circleId, isSelected) => {
    const newSelected = new Set(selectedPolygonCircles);
    if (isSelected) {
      newSelected.add(circleId);
    } else {
      newSelected.delete(circleId);
    }
    setSelectedPolygonCircles(newSelected);
  };

  const selectAllPolygonCircles = () => {
    const allCircleIds = new Set(generatedCircles.map(circle => circle.id));
    setSelectedPolygonCircles(allCircleIds);
  };

  const deselectAllPolygonCircles = () => {
    setSelectedPolygonCircles(new Set());
  };


const handlePolygonSearch = async () => {
  if (selectedPolygonCircles.size === 0) {
    toast.error('Please select at least one circle to search');
    return;
  }

  // Validate based on search mode
  if (filterSearchMode === 'category') {
    if (includedTypes.length === 0) {
      toast.error('Please specify at least one place type to search for');
      return;
    }
  } else if (filterSearchMode === 'text') {
    if (!textQuery.trim()) {
      toast.error('Please enter a text query to search for');
      return;
    }
  }

  const remaining = getRemainingRequests();
  const circlesCount = selectedPolygonCircles.size;
  
  if (remaining < circlesCount) {
    toast.error(`Not enough API requests remaining. You need ${circlesCount} requests but only have ${remaining} remaining.`);
    return;
  }

  setIsSearching(true);
  setSearchResults([]);
  setCircleAnalysis(null);
  setChainDetection(null);

  const selectedCircleObjects = generatedCircles.filter(circle => 
    selectedPolygonCircles.has(circle.id)
  );

  const shapeData = {
    type: searchMode === 'polygon' ? 'pentagon' : 'tube',
    center: currentShape.center,
    outerPoints: currentShape.outerPoints,
    outerRadius: currentShape.outerRadius || shapeSize,
    ...(searchMode === 'tube' && {
      innerPoints: currentShape.innerPoints,
      innerRadius: currentShape.innerRadius || (shapeSize - tubeThickness),
      thickness: currentShape.thickness || tubeThickness
    })
  };

  const searchData = {
    search_areas: selectedCircleObjects.map(circle => ({
      latitude: circle.center_point.latitude,
      longitude: circle.center_point.longitude,
      radius: circle.radius,
    })),
    shape_type: searchMode === 'polygon' ? 'pentagon' : 'tube',
    shape_data: shapeData
  };

  // FIXED: Conditionally add fields based on search mode
  if (filterSearchMode === 'category') {
    searchData.included_types = includedTypes;
    searchData.excluded_types = excludedTypes;
  } else if (filterSearchMode === 'text') {
    // For text search, ONLY send text_query, NO included_types
    searchData.text_query = textQuery.trim();
    // Don't send included_types at all - let text search work purely on the query
  }

  const { data, error } = await leadGenService.searchPlaces(searchData);
  
  if (data) {
    setSearchResults(data.places || []);
    setLastSearchSession(data.search_session);
    setHasSearchedThisSession(true);
    
    if (data.searched_shape) {
      const searchedShapeObj = {
        id: data.searched_shape.id,
        shape_type: data.searched_shape.shape_type,
        shape_data: data.searched_shape.shape_data || shapeData,
        has_been_searched: true,
        last_searched_at: new Date().toISOString()
      };
      
      setSearchedShapes(prev => {
        const filtered = prev.filter(shape => shape.id !== searchedShapeObj.id);
        return [...filtered, searchedShapeObj];
      });
      
      setCurrentShape(null);
      setGeneratedCircles([]);
      setSelectedPolygonCircles(new Set());
      setShapeHistory([]);
      setHistoryIndex(-1);
      
      toast.success(`${searchMode === 'polygon' ? 'Pentagon' : 'Tube'} search completed and saved!`);
    } else {
      console.warn('No searched_shape returned from backend, creating temporary one');
      const tempShape = {
        id: `temp-${Date.now()}`,
        shape_type: searchMode === 'polygon' ? 'pentagon' : 'tube',
        shape_data: shapeData,
        has_been_searched: true,
        last_searched_at: new Date().toISOString()
      };
      
      setSearchedShapes(prev => [...prev, tempShape]);
      setCurrentShape(null);
      setGeneratedCircles([]);
      setSelectedPolygonCircles(new Set());
      setShapeHistory([]);
      setHistoryIndex(-1);
    }
    
    if (data.circle_analysis) {
      setCircleAnalysis(data.circle_analysis);
      
      if (data.circle_analysis.circles_at_limit > 0) {
        toast(data.circle_analysis.message, {
          duration: 6000,
          icon: '⚠️',
          style: {
            background: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fbbf24',
          },
        });
      }
    }

    if (data.chain_detection) {
      setChainDetection(data.chain_detection);
      
      if (data.chain_detection.unique_chain_count > 0) {
        toast(`🔗 Found ${data.chain_detection.unique_chain_count} chains with ${data.chain_detection.total_chain_places} locations`, {
          duration: 5000,
          style: {
            background: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #3b82f6',
          },
        });
      }

      if (data.chain_detection.auto_excluded_count > 0) {
        toast(`🚫 ${data.chain_detection.auto_excluded_count} places automatically excluded based on your settings`, {
          duration: 4000,
          style: {
            background: '#fef2f2',
            color: '#991b1b',
            border: '1px solid #ef4444',
          },
        });
      }
    }
    
    const searchModeText = filterSearchMode === 'text' ? ` for "${textQuery.trim()}"` : '';
    toast.success(`Found ${data.places?.length || 0} places${searchModeText}`);
    
    setTimeout(() => {
      searchResultsRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  } else if (error) {
    console.error('Full error object:', error);
    toast.error(error.message || 'Search failed');
  }

  setIsSearching(false);
};

  const showConfirmation = ({ title, message, onConfirm, type = 'default', confirmText = 'OK', cancelText = 'Cancel' }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      type,
      confirmText,
      cancelText
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {},
      type: 'default',
      confirmText: 'OK',
      cancelText: 'Cancel'
    });
  };

  useEffect(() => {
    loadSearchAreas();
    setHasInitialized(true);
  }, []);

  useEffect(() => {
    if (map) {
      const currentZoom = map.getZoom();
      map.setZoom(currentZoom + 0.001);
      setTimeout(() => {
        map.setZoom(currentZoom);
      }, 10);
    }
  }, [searchedAreas.length, unsearchedAreas.length, map]);

  const loadSearchAreas = async () => {
    const { data, error } = await leadGenService.getSearchAreas();
    if (data) {
      setSearchedAreas(data.searched_areas?.areas || []);
      setUnsearchedAreas(data.unsearched_areas?.areas || []);
    } else if (error) {
      console.error('Error loading search areas:', error);
    }
  };

  const getAllAreas = () => {
    return [...searchedAreas, ...unsearchedAreas];
  };  

  const handleMapClick = async (event) => {
    if (isShapeDragging) return;
    
    if (searchMode === 'circles') {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      
      const newArea = {
        latitude: parseFloat(lat.toFixed(7)),
        longitude: parseFloat(lng.toFixed(7)),
        radius: currentRadius,
      };

      const { data, error } = await leadGenService.createSearchArea(newArea);
      if (data) {
        setUnsearchedAreas(prev => [...prev, data]);
        setSelectedArea(data);
        toast.success('Search area added');
      } else if (error) {
        console.error('Error creating search area:', error);
        toast.error('Failed to add search area');
      }
    } else if ((searchMode === 'polygon' || searchMode === 'tube') && isDrawingMode) {
      const center = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      
      let newShape;
      
      if (searchMode === 'polygon') {
        const outerPoints = createPentagonPoints(center, shapeSize);
        newShape = {
          type: 'pentagon',
          center,
          outerPoints,
          outerRadius: shapeSize
        };
      } else if (searchMode === 'tube') {
        const outerPoints = createPentagonPoints(center, shapeSize);
        const innerPoints = createPentagonPoints(center, shapeSize - tubeThickness);
        newShape = {
          type: 'tube',
          center,
          outerPoints,
          innerPoints,
          outerRadius: shapeSize,
          thickness: tubeThickness
        };
      }
      
      setCurrentShape(newShape);
      setShapeHistory([{
        shape: JSON.parse(JSON.stringify(newShape)),
        actionType: 'create',
        timestamp: Date.now()
      }]);
      setHistoryIndex(0);
      setIsDrawingMode(false);
      
      toast.success(`${searchMode === 'polygon' ? 'Pentagon' : 'Tube'} created! Press 'M' to toggle between Move/Reshape modes.`);
    }
  };

  const handleRadiusChange = (value) => {
    const radiusValue = parseInt(value);
    setCurrentRadius(radiusValue);
    localStorage.setItem('leadgen_radius', radiusValue.toString());
  };

  const handlePolygonRadiusChange = (value) => {
    const radiusValue = parseInt(value);
    setPolygonRadius(radiusValue);
    localStorage.setItem('leadgen_polygon_radius', radiusValue.toString());
  };

  const handleCustomRadiusSubmit = () => {
    const value = parseInt(customRadiusInput);
    if (isNaN(value) || value < 50 || value > 50000) {
      toast.error('Please enter a radius between 50 and 50,000 meters');
      return;
    }
    handleRadiusChange(value);
    setCustomRadiusInput('');
    setShowCustomRadiusInput(false);
    toast.success(`Radius set to ${value}m`);
  };

  const handleCustomPolygonRadiusSubmit = () => {
    const value = parseInt(customPolygonRadiusInput);
    if (isNaN(value) || value < 50 || value > 50000) {
      toast.error('Please enter a radius between 50 and 50,000 meters');
      return;
    }
    handlePolygonRadiusChange(value);
    setCustomPolygonRadiusInput('');
    setShowCustomPolygonRadiusInput(false);
    toast.success(`Polygon circle radius set to ${value}m`);
  };

  const addAdjacentArea = async (direction) => {
    AreaUtils.addAdjacentArea(selectedArea, direction, setUnsearchedAreas, setSelectedArea);
  };

  const deleteSelectedArea = async () => {
    AreaUtils.deleteSelectedArea(selectedArea, setSearchedAreas, setUnsearchedAreas, setSelectedArea);
  };

  const deleteLatestUnsearchedArea = async () => {
    AreaUtils.deleteLatestUnsearchedArea(unsearchedAreas, selectedArea, setUnsearchedAreas, setSelectedArea);
  };

  const clearUnsearchedAreas = async () => {
    AreaUtils.clearUnsearchedAreas(unsearchedAreas, setUnsearchedAreas, setSelectedArea, loadSearchAreas, showConfirmation);
  };

  const deleteLatestSearchedArea = async () => {
    AreaUtils.deleteLatestSearchedArea(searchedAreas, selectedArea, setSearchedAreas, setSelectedArea);
  };

  const deleteAllSearchedAreas = async () => {
    AreaUtils.deleteAllSearchedAreas(searchedAreas, setSearchedAreas, setSelectedArea, loadSearchAreas, showConfirmation);
  };

  const addIncludedType = (type) => {
    AreaUtils.addIncludedType(type, includedTypes, setIncludedTypes);
  };

  const removeIncludedType = (index) => {
    AreaUtils.removeIncludedType(index, setIncludedTypes);
  };

  const addExcludedType = (type) => {
    AreaUtils.addExcludedType(type, excludedTypes, setExcludedTypes)
  };

  const removeExcludedType = (index) => {
    AreaUtils.removeExcludedType(index, setExcludedTypes);
  };

  const navigateToCity = (city) => {
    setMapCenter({ lat: city.lat, lng: city.lng });
    if (map) {
      map.panTo({ lat: city.lat, lng: city.lng });
    }
    toast.success(`Navigated to ${city.name}`);
  };



// FINAL CORRECTED handleSearch function

const handleSearch = async () => {
  if (unsearchedAreas.length === 0) {
    toast.error('Please add at least one unsearched area');
    return;
  }

  // Validate based on search mode
  if (filterSearchMode === 'category') {
    if (includedTypes.length === 0) {
      toast.error('Please specify at least one place type to search for');
      return;
    }
  } else if (filterSearchMode === 'text') {
    if (!textQuery.trim()) {
      toast.error('Please enter a text query to search for');
      return;
    }
  }

  const remaining = getRemainingRequests();
  const circlesCount = unsearchedAreas.length;

  if (remaining < circlesCount) {
    toast.error(`Not enough API requests remaining. You need ${circlesCount} requests but only have ${remaining} remaining.`);
    return;
  }

  setIsSearching(true);
  setSearchResults([]);
  setCircleAnalysis(null);
  setChainDetection(null);

  const searchData = {
    search_areas: unsearchedAreas.map(area => ({
      latitude: parseFloat(area.latitude),
      longitude: parseFloat(area.longitude),
      radius: area.radius,
    })),
  };

  // FIXED: Conditionally add fields based on search mode
  if (filterSearchMode === 'category') {
    searchData.included_types = includedTypes;
    searchData.excluded_types = excludedTypes;
  } else if (filterSearchMode === 'text') {
    // For text search, ONLY send text_query, NO included_types
    searchData.text_query = textQuery.trim();
    // Don't send included_types at all - let text search work purely on the query
  }

  const { data, error } = await leadGenService.searchPlaces(searchData);

  if (data) {
    setSearchResults(data.places || []);
    setLastSearchSession(data.search_session);
    setHasSearchedThisSession(true);
    
    if (data.circle_analysis) {
      setCircleAnalysis(data.circle_analysis);
      
      if (data.circle_analysis.circles_at_limit > 0) {
        toast(data.circle_analysis.message, {
          duration: 6000,
          icon: '⚠️',
          style: {
            background: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fbbf24',
          },
        });
      }
    }

    if (data.chain_detection) {
      setChainDetection(data.chain_detection);
      
      if (data.chain_detection.unique_chain_count > 0) {
        toast(`🔗 Found ${data.chain_detection.unique_chain_count} chains with ${data.chain_detection.total_chain_places} locations`, {
          duration: 5000,
          style: {
            background: '#dbeafe',
            color: '#1e40af',
            border: '1px solid #3b82f6',
          },
        });
      }

      if (data.chain_detection.auto_excluded_count > 0) {
        toast(`🚫 ${data.chain_detection.auto_excluded_count} places automatically excluded based on your settings`, {
          duration: 4000,
          style: {
            background: '#fef2f2',
            color: '#991b1b',
            border: '1px solid #ef4444',
          },
        });
      }
    }
    
    if (data.searched_areas) {
      setSearchedAreas(prev => [...prev, ...data.searched_areas]);
      setUnsearchedAreas(prev => 
        prev.filter(area => !data.searched_areas.some(searched => searched.id === area.id))
      );
    }
    
    const searchModeText = filterSearchMode === 'text' ? ` for "${textQuery.trim()}"` : '';
    toast.success(`Found ${data.places?.length || 0} places${searchModeText}`);
    
    setTimeout(() => {
      searchResultsRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  } else if (error) {
    console.error('Full error object:', error);
    toast.error(error.message || 'Search failed');
  }

  setIsSearching(false);
};

  const handleSavePlaces = async () => {
    if (searchResults.length === 0) {
      toast.error('No places to save');
      return;
    }

    setIsSaving(true);

    const { data, error } = await leadGenService.savePlaces(
      searchResults,
      lastSearchSession?.id
    );

    if (data) {
      toast.success(`Saved ${data.new_places_added || searchResults.length} places! ${data.total_existing > 0 ? `(${data.total_existing} already existed)` : ''}`);
      setSearchResults([]);
      setLastSearchSession(null);
      storageService.removeUnsavedSearchResults();
      setHasSearchedThisSession(false);
      
      // Show social media notification for all users
      setShowSocialMediaNotification(true);
    } else if (error) {
      toast.error('Failed to save places');
      console.error('Error saving places:', error);
    }

    setIsSaving(false);
  };

  const handlePlaceCleanup = async (placeIds, exclusionPatterns) => {
    setSearchResults(prev => prev.filter(place => !placeIds.includes(place.id)));

    if (exclusionPatterns.length > 0) {
      const { data, error } = await leadGenService.addExclusionPatterns(exclusionPatterns);
      
      if (data) {
        toast.success(`${placeIds.length} places removed${data.total_added > 0 ? ` and ${data.total_added} exclusion patterns added` : ''}`);
      } else if (error) {
        toast.error('Failed to add exclusion patterns');
      }
    } else {
      toast.success(`${placeIds.length} places removed`);
    }
  };

  if (!isLoaded) return <Loading />;

  return (
    <div className="h-screen flex flex-col">
      {guideData && (
        <GuideSystem
          steps={guideData.steps}
          pageName={guideData.pageName}
          isVisible={isGuideVisible}
          onClose={hideGuide}
        />
      )}

      <FloatingGuideButton 
        onClick={showGuide}
        hasNewFeature={!hasSeenGuide}
      />

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
      />

      {showAnalysisModal && circleAnalysis && (
        <CircleAnalysisModal 
          analysis={circleAnalysis} 
          onClose={() => setShowAnalysisModal(false)} 
        />
      )}

      {showChainModal && chainDetection && (
        <ChainDetectionModal 
          chainDetection={chainDetection}
          searchResults={searchResults}
          onCleanup={handlePlaceCleanup}
          onClose={() => setShowChainModal(false)} 
        />
      )}

      {/* City Navigation Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex flex-wrap gap-2 justify-between items-center">
            {/* Home Icon */}
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title="Go to Dashboard"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          <div className="flex items-center gap-2">
            
            {/* City buttons */}
            <div className="flex flex-wrap gap-2">
              {CITIES.map((city, index) => (
                <button
                  key={index}
                  onClick={() => navigateToCity(city)}
                  className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 transition-colors"
                >
                  {city.name}
                </button>
              ))}
            </div>
          </div>
          
          <button
            onClick={() => navigate('/places')}
            className="px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded border border-blue-600 transition-colors whitespace-nowrap"
          >
            Saved Places
          </button>
        </div>
      </div>
      
      <div className="flex h-[calc(100vh-60px)]">
        <SearchSidebar
          searchMode={searchMode}
          setSearchMode={setSearchMode}
          handleShapeDelete={handleShapeDelete}
          currentShape={currentShape}
          shapeInteractionMode={shapeInteractionMode}
          setShapeInteractionMode={setShapeInteractionMode}
          isDrawingMode={isDrawingMode}
          setIsDrawingMode={setIsDrawingMode}
          shapeSize={shapeSize}
          setShapeSize={setShapeSize}
          tubeThickness={tubeThickness}
          setTubeThickness={setTubeThickness}
          undoShapeChange={undoShapeChange}
          historyIndex={historyIndex}
          isGeneratingCircles={isGeneratingCircles}
          selectedPolygonCircles={selectedPolygonCircles}
          currentRadius={currentRadius}
          handleRadiusChange={handleRadiusChange}
          selectedArea={selectedArea}
          deleteSelectedArea={deleteSelectedArea}
          addAdjacentArea={addAdjacentArea}
          includedTypes={includedTypes}
          excludedTypes={excludedTypes}
          addIncludedType={addIncludedType}
          removeIncludedType={removeIncludedType}
          addExcludedType={addExcludedType}
          removeExcludedType={removeExcludedType}
          formatPlaceTypeDisplay={formatPlaceTypeDisplay}
          handlePolygonSearch={handlePolygonSearch}
          handleSearch={handleSearch}
          isSearching={isSearching}
          unsearchedAreas={unsearchedAreas}
          getAreaLimit={getAreaLimit}
          getRemainingRequests={getRemainingRequests}
          getRequestLimit={getRequestLimit}
          getUserType={getUserType}
          showCustomPolygonRadiusInput={showCustomPolygonRadiusInput}
          setShowCustomPolygonRadiusInput={setShowCustomPolygonRadiusInput}
          customPolygonRadiusInput={customPolygonRadiusInput}
          handleCustomPolygonRadiusSubmit={handleCustomPolygonRadiusSubmit}
          polygonRadius={polygonRadius}
          handlePolygonRadiusChange={handlePolygonRadiusChange}
          handleGenerateShapeCircles={handleGenerateShapeCircles}
          generatedCircles={generatedCircles}
          selectAllPolygonCircles={selectAllPolygonCircles}
          deselectAllPolygonCircles={deselectAllPolygonCircles}
          searchedAreas={searchedAreas}
          visibleAreas={visibleAreas}
          deleteLatestUnsearchedArea={deleteLatestUnsearchedArea}
          clearUnsearchedAreas={clearUnsearchedAreas}
          deleteLatestSearchedArea={deleteLatestSearchedArea}
          deleteAllSearchedAreas={deleteAllSearchedAreas}
          showCustomRadiusInput={showCustomRadiusInput}
          customRadiusInput={customRadiusInput}
          setCustomRadiusInput={setCustomRadiusInput}
          handleCustomRadiusSubmit={handleCustomRadiusSubmit}
          setShowCustomRadiusInput={setShowCustomRadiusInput}
          setCustomPolygonRadiusInput={setCustomPolygonRadiusInput}
          filterSearchMode={filterSearchMode}
          setFilterSearchMode={setFilterSearchMode}
          textQuery={textQuery}
          setTextQuery={setTextQuery}
        />

        <div className="flex-1 relative">
          <MapView
            mapContainerStyle={{ width: '100%', height: '100%' }}
            mapCenter={mapCenter}
            map={map}
            setMap={setMap}
            setMapBounds={setMapBounds}
            handleMapClick={handleMapClick}
            searchMode={searchMode}
            visibleAreas={visibleAreas}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            searchedShapes={searchedShapes}
            isDrawingMode={isDrawingMode}
            isShapeDragging={isShapeDragging}
            currentShape={currentShape}
            shapeInteractionMode={shapeInteractionMode}
            setIsDragging={setIsDragging}
            generatedCircles={generatedCircles}
            selectedPolygonCircles={selectedPolygonCircles}
            handlePolygonCircleSelection={handlePolygonCircleSelection}
            shapeDragStart={shapeDragStart}
            shapeSize={shapeSize}
            tubeThickness={tubeThickness}
            setCurrentShape={setCurrentShape}
            shapeHistory={shapeHistory}
            historyIndex={historyIndex}
            setShapeHistory={setShapeHistory}
            setHistoryIndex={setHistoryIndex}
            setIsShapeDragging={setIsShapeDragging}
            setShapeDragStart={setShapeDragStart}
            handleDeleteCircle={handleDeleteSpecificArea}
          />

          {searchedShapes.length > 0 && (
            <div className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg border">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Shape Legend</h4>
              <div className="space-y-1 text-xs">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-purple-500 opacity-20 border border-purple-500 rounded mr-2"></div>
                  <span>Active Pentagon</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-orange-500 opacity-15 border border-orange-500 rounded mr-2"></div>
                  <span>Active Tube</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-500 opacity-10 border border-gray-500 rounded mr-2"></div>
                  <span>Previously Searched</span>
                </div>
              </div>
              {searchedShapes.filter(s => s.has_been_searched).length > 0 && (
                <div className="mt-2 text-xs text-gray-600">
                  {searchedShapes.filter(s => s.has_been_searched).length} searched areas
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {searchResults.length > 0 && (
        <SearchResultsPanel
          results={searchResults}
          chainDetection={chainDetection}
          circleAnalysis={circleAnalysis}
          isSaving={isSaving}
          onSave={handleSavePlaces}
          onRemovePlace={handlePlaceCleanup}
          onShowChainModal={() => setShowChainModal(true)}
          onShowAnalysisModal={() => setShowAnalysisModal(true)}
          searchResultsRef={searchResultsRef}
        />
      )}
    </div>
  );
};

export default SearchPage;